/* NurseAttendance.js — Admin view for managing all nurses' monthly attendance */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { CheckCircle2, Clock4, XCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────

function todayStr() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function hoursWorked(rec) {
  if (!rec?.check_in_at || !rec?.check_out_at) return null;
  return ((new Date(rec.check_out_at) - new Date(rec.check_in_at)) / 36e5).toFixed(1);
}

function classifyDay(rec) {
  if (!rec?.check_in_at) return 'absent';
  if (rec.check_in_at && rec.check_out_at) {
    const hrs = (new Date(rec.check_out_at) - new Date(rec.check_in_at)) / 36e5;
    return hrs >= 8 ? 'full' : 'half';
  }
  return 'half';
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function buildCalendarCells(month, byDate) {
  const [year, mon] = month.split('-').map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const firstDow = new Date(year, mon - 1, 1).getDay();
  const today = todayStr();
  const isCurrentMonth = today.slice(0, 7) === month;
  const todayDay = Number(today.slice(8, 10));

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${month}-${String(d).padStart(2, '0')}`;
    const rec = byDate[dateStr];
    const dow = new Date(dateStr).getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isFuture = isCurrentMonth && d > todayDay;
    const isToday = isCurrentMonth && d === todayDay;
    const status = isWeekend ? 'weekend' : isFuture ? 'future' : classifyDay(rec);
    cells.push({ d, dateStr, rec, isWeekend, isFuture, isToday, status });
  }
  return cells;
}

function computeStats(month, byDate) {
  const [year, mon] = month.split('-').map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const today = todayStr();
  const isCurrentMonth = today.slice(0, 7) === month;
  const cutoffDay = isCurrentMonth ? Number(today.slice(8, 10)) : daysInMonth;

  let fullDays = 0, halfDays = 0, absentDays = 0, weekdays = 0;
  for (let d = 1; d <= cutoffDay; d++) {
    const dow = new Date(year, mon - 1, d).getDay();
    if (dow === 0 || dow === 6) continue;
    weekdays++;
    const dateStr = `${month}-${String(d).padStart(2, '0')}`;
    const status = classifyDay(byDate[dateStr]);
    if (status === 'full') fullDays++;
    else if (status === 'half') halfDays++;
    else absentDays++;
  }
  const workRate = weekdays > 0 ? Math.round(((fullDays + halfDays * 0.5) / weekdays) * 100) : 0;
  return { fullDays, halfDays, absentDays, weekdays, workRate };
}

// ── Mini calendar (read-only) ─────────────────────────────────────────────────
const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function MiniCalendar({ cells, month, onMarkDay }) {
  const cellStyle = (status, isToday) => {
    const base = 'w-full aspect-square flex items-center justify-center rounded-md text-[10px] font-semibold cursor-pointer select-none transition-transform hover:scale-110 ';
    const todayRing = isToday ? 'ring-2 ring-[#f37721] ring-offset-1 ' : '';
    switch (status) {
      case 'full':    return base + todayRing + 'bg-emerald-100 text-emerald-800';
      case 'half':    return base + todayRing + 'bg-amber-100 text-amber-800';
      case 'absent':  return base + todayRing + 'bg-red-50 text-red-500';
      case 'weekend': return base + 'bg-[#f3f4f6] text-[#9ca3af] cursor-default';
      case 'future':  return base + 'bg-[#f9fafb] text-[#d1d5db] cursor-default';
      default:        return base;
    }
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {DOW_LABELS.map((l) => (
          <div key={l} className="text-center text-[9px] font-semibold text-[#9ca3af] py-0.5">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((cell, i) =>
          cell === null ? <div key={`blank-${i}`} /> : (
            <div
              key={cell.dateStr}
              className={cellStyle(cell.status, cell.isToday)}
              title={
                cell.isWeekend ? 'Weekend' :
                cell.isFuture ? 'Future' :
                `${cell.dateStr} — ${cell.status === 'full' ? `Full Day (${hoursWorked(cell.rec)}h)` : cell.status === 'half' ? `Half Day` : 'Absent'} — click to change`
              }
              onClick={() => {
                if (cell.isWeekend || cell.isFuture) return;
                onMarkDay(cell);
              }}
            >
              {cell.d}
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ── Mark Day Modal ────────────────────────────────────────────────────────────
function MarkDayModal({ cell, nurseName, onMark, onClose, loading }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6 space-y-4">
        <div>
          <h3 className="font-bold text-[#311b52] text-base" style={{ fontFamily: 'Poppins' }}>Mark Attendance</h3>
          <p className="text-xs text-[#6b7280] mt-1">{nurseName} · {fmtDate(cell.dateStr)}</p>
          <p className="text-[11px] mt-1 text-[#9ca3af]">Current: <span className="capitalize font-semibold">{cell.status}</span></p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { action: 'full', label: 'Full Day', sub: '9–18 h', icon: '✅', cls: 'border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' },
            { action: 'half',    label: 'Half Day', sub: '9–13 h', icon: '⏳', cls: 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100' },
            { action: 'absent',  label: 'Absent',   sub: 'remove', icon: '❌', cls: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' },
          ].map(({ action, label, sub, icon, cls }) => (
            <button
              key={action}
              type="button"
              disabled={loading}
              onClick={() => onMark(action)}
              className={`border-2 rounded-xl p-3 flex flex-col items-center gap-1 text-center transition-all disabled:opacity-50 ${cls}`}
            >
              <span className="text-xl">{icon}</span>
              <span className="text-xs font-bold">{label}</span>
              <span className="text-[9px] opacity-70">{sub}</span>
            </button>
          ))}
        </div>
        <button type="button" onClick={onClose} className="w-full rounded-full border border-[#e5e7eb] py-2 text-sm text-[#6b7280]">Cancel</button>
      </div>
    </div>
  );
}

// ── Nurse row ─────────────────────────────────────────────────────────────────
function NurseRow({ nurse, attendanceItems, month, onMarkDay }) {
  const [expanded, setExpanded] = useState(false);

  const byDate = {};
  attendanceItems.forEach((a) => { byDate[a.date] = a; });

  const cells = buildCalendarCells(month, byDate);
  const stats = computeStats(month, byDate);

  const monthName = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
  })();

  return (
    <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#faf9fc] transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-[#673c96]/15 text-[#673c96] flex items-center justify-center text-sm font-bold shrink-0">
          {(nurse.full_name || 'N').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#311b52] text-sm">{nurse.full_name || 'Unknown'}</p>
          <p className="text-xs text-[#6b7280]">{nurse.phone || nurse.email || nurse.id}</p>
        </div>
        {/* Mini stats */}
        <div className="hidden sm:flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> {stats.fullDays}
          </span>
          <span className="flex items-center gap-1 font-semibold text-amber-600">
            <Clock4 className="w-3.5 h-3.5" /> {stats.halfDays}
          </span>
          <span className="flex items-center gap-1 font-semibold text-red-500">
            <XCircle className="w-3.5 h-3.5" /> {stats.absentDays}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-[#673c96]/10 text-[#673c96] font-bold">
            {stats.workRate}%
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#673c96] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#673c96] shrink-0" />}
      </div>

      {expanded && (
        <div className="border-t border-[#f3f4f6] px-5 py-5 space-y-5">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Full Days', value: stats.fullDays, sub: '≥ 8h', color: 'from-emerald-500 to-green-600', icon: '✅' },
              { label: 'Half Days', value: stats.halfDays, sub: '< 8h', color: 'from-amber-400 to-orange-500', icon: '⏳' },
              { label: 'Absent', value: stats.absentDays, sub: 'days', color: 'from-red-400 to-rose-600', icon: '❌' },
              { label: 'Work Rate', value: `${stats.workRate}%`, sub: 'of weekdays', color: 'from-[#673c96] to-[#311b52]', icon: '📊' },
            ].map(({ label, value, sub, color, icon }) => (
              <div key={label} className={`bg-gradient-to-br ${color} rounded-xl p-3 text-white shadow-sm`}>
                <div className="text-lg mb-0.5">{icon}</div>
                <p className="text-xl font-bold">{value}</p>
                <p className="text-[10px] font-semibold opacity-90">{label}</p>
                <p className="text-[9px] opacity-70">{sub}</p>
              </div>
            ))}
          </div>

          {/* Calendar */}
          <div>
            <p className="text-xs font-bold text-[#311b52] mb-2" style={{ fontFamily: 'Poppins' }}>
              Month View — {monthName}
              <span className="font-normal text-[#9ca3af] ml-2">Click any weekday to mark attendance</span>
            </p>
            <div className="flex gap-3 flex-wrap mb-2">
              {[
                { color: 'bg-emerald-100', label: 'Full Day' },
                { color: 'bg-amber-100', label: 'Half Day' },
                { color: 'bg-red-50', label: 'Absent' },
                { color: 'bg-[#f3f4f6]', label: 'Weekend' },
                { color: 'bg-[#f9fafb]', label: 'Future' },
              ].map(({ color, label }) => (
                <span key={label} className="flex items-center gap-1 text-[10px] text-[#6b7280]">
                  <span className={`w-2.5 h-2.5 rounded ${color}`} /> {label}
                </span>
              ))}
            </div>
            <MiniCalendar
              cells={cells}
              month={month}
              onMarkDay={(cell) => onMarkDay(nurse, cell)}
            />
          </div>

          {/* Detailed list */}
          {attendanceItems.length > 0 && (
            <div className="divide-y divide-[#f3f4f6]">
              {attendanceItems.map((a) => {
                const hrs = hoursWorked(a);
                const status = classifyDay(a);
                return (
                  <div key={a.date} className="flex items-center justify-between py-2 text-xs gap-2">
                    <span className="font-medium text-[#311b52] w-24 shrink-0">{fmtDate(a.date)}</span>
                    <span className="text-[#6b7280]">
                      {a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '—'}
                      {' → '}
                      {a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '—'}
                      {hrs && ` (${hrs}h)`}
                      {a.marked_by_admin && <span className="ml-1 text-[#673c96]">· admin</span>}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold shrink-0 ${status === 'full' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {status === 'full' ? 'Full' : 'Half'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function NurseAttendance() {
  const { getAuthHeaders } = useAuth();
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markTarget, setMarkTarget] = useState(null); // { nurse, cell }
  const [marking, setMarking] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: d } = await axios.get(`${config.API}/nurse/admin/attendance`, {
        params: { month },
        headers: getAuthHeaders(),
      });
      setData(d);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [month, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleMark = async (action) => {
    if (!markTarget) return;
    setMarking(true);
    try {
      await axios.post(`${config.API}/nurse/admin/attendance/mark`, {
        nurse_id: markTarget.nurse.id,
        nurse_name: markTarget.nurse.full_name,
        date: markTarget.cell.dateStr,
        action,
      }, { headers: getAuthHeaders() });
      setMsg(`Marked ${markTarget.nurse.full_name} as ${action} on ${fmtDate(markTarget.cell.dateStr)}`);
      setMarkTarget(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setMarking(false);
    }
  };

  // Build per-nurse maps
  const nurses = data?.nurses || [];
  const allItems = data?.items || [];
  const itemsByNurse = {};
  allItems.forEach((a) => {
    if (!itemsByNurse[a.nurse_id]) itemsByNurse[a.nurse_id] = [];
    itemsByNurse[a.nurse_id].push(a);
  });

  // Overall stats
  const totalFull = Object.values(itemsByNurse).flat().filter((a) => classifyDay(a) === 'full').length;
  const totalHalf = Object.values(itemsByNurse).flat().filter((a) => classifyDay(a) === 'half').length;

  // CSV export
  const exportCsv = () => {
    const rows = [['Nurse', 'Phone', 'Date', 'Check In', 'Check Out', 'Hours', 'Status', 'Admin Marked']];
    nurses.forEach((n) => {
      const items = itemsByNurse[n.id] || [];
      items.forEach((a) => {
        const hrs = hoursWorked(a) || '';
        rows.push([
          n.full_name || '',
          n.phone || '',
          a.date,
          a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
          a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
          hrs,
          classifyDay(a),
          a.marked_by_admin ? 'Yes' : 'No',
        ]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const monthName = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Nurse Attendance</h1>
          <p className="text-sm text-[#6b7280]">View, verify and manually mark attendance for payroll · {monthName}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-xl border border-[#673c96]/20 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#311b52] text-white text-sm font-semibold"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary banner */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Active Nurses', value: nurses.length, color: 'from-[#673c96] to-[#311b52]', icon: '👩‍⚕️' },
            { label: 'Full Days Logged', value: totalFull, color: 'from-emerald-500 to-green-600', icon: '✅' },
            { label: 'Half Days Logged', value: totalHalf, color: 'from-amber-400 to-orange-500', icon: '⏳' },
            { label: 'Total Records', value: allItems.length, color: 'from-blue-500 to-indigo-600', icon: '📋' },
          ].map(({ label, value, color, icon }) => (
            <div key={label} className={`bg-gradient-to-br ${color} rounded-2xl p-4 text-white shadow-sm`}>
              <div className="text-2xl mb-1">{icon}</div>
              <p className="text-2xl font-bold">{value}</p>
              <p className="text-xs font-semibold opacity-90">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      {msg && (
        <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          <span>✅ {msg}</span>
          <button onClick={() => setMsg('')} className="text-emerald-600 font-bold">✕</button>
        </div>
      )}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-16 text-[#6b7280] text-sm">Loading attendance data…</div>
      )}

      {/* Legend */}
      {!loading && data && (
        <p className="text-xs text-[#9ca3af]">
          💡 <strong>Full Day</strong> = ≥ 8 hours checked in+out &nbsp;·&nbsp;
          <strong>Half Day</strong> = &lt; 8 hours &nbsp;·&nbsp;
          Click any weekday cell in a nurse's calendar to mark or change attendance.
        </p>
      )}

      {/* Nurse rows */}
      {!loading && data && (
        <div className="space-y-3">
          {nurses.length === 0 && (
            <p className="text-sm text-[#6b7280] text-center py-8">No active nurses found.</p>
          )}
          {nurses.map((nurse) => (
            <NurseRow
              key={nurse.id}
              nurse={nurse}
              attendanceItems={itemsByNurse[nurse.id] || []}
              month={month}
              onMarkDay={(n, cell) => setMarkTarget({ nurse: n, cell })}
            />
          ))}
        </div>
      )}

      {/* Mark Day Modal */}
      {markTarget && (
        <MarkDayModal
          cell={markTarget.cell}
          nurseName={markTarget.nurse.full_name}
          onMark={handleMark}
          onClose={() => setMarkTarget(null)}
          loading={marking}
        />
      )}
    </div>
  );
}
